// The shape every reinsurance module screen takes: header, four KPI cards, a
// filter bar, a table, and a drawer that opens on the selected row.
//
// Each screen supplies its endpoint, its KPI definitions, its columns, its
// filters and its drawer panels. Everything else — fetching, searching,
// paging, selection, the empty and error states — is here, once.
//
// KPIs come from `${endpoint}/stats`, not from the loaded page: a page holds
// fifty rows and the headline figures are about the whole book. Deriving them
// from what happens to be on screen is how a dashboard quietly starts lying
// as soon as the data outgrows one page.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import {
  BASE_URL, C, S, PageHeader, KpiRow, FilterBar, DataTable, Drawer,
} from './kit';
import CreateForm from './CreateForm';

export default function ModulePage({
  title,
  subtitle,
  endpoint,
  searchPlaceholder,
  filters = [],
  columns,
  actions,
  renderKpis,
  drawer,                 // (row, { tab, setTab, reload }) => ReactNode
  drawerTabs = [],
  drawerHeader,           // (row) => { title, badge, lines }
  // Create form, rendered in the drawer. See CreateForm.js. When present,
  // `actions` is called with { openCreate } so the screen's own header
  // button opens it — the button belongs to the screen, the panel does not.
  createForm,
  emptyMessage = 'Nothing here yet.',
  initialQuery = {},
  pageSize = 50,
  defaultSort,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // Which fields the SERVER can order by, from the list response. The table
  // offers a sort control on these and nothing else, so a composite column
  // cannot advertise an order the database will silently discard.
  const [sortable, setSortable] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  // Filters seed from the URL, so a link can arrive pre-filtered: the
  // dashboard's "Outstanding Closings" is not every closing, and landing on
  // the unfiltered list would show a different number from the one clicked.
  const [values, setValues] = useState(() => {
    const q = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const out = {};
    for (const f of filters) {
      // A date range is two keys, and its empty value is '' — not 'all',
      // which would be sent to the server as a date.
      if (f.type === 'daterange') {
        const fk = f.fromKey || 'from';
        const tk = f.toKey || 'to';
        out[fk] = q.get(fk) || '';
        out[tk] = q.get(tk) || '';
      } else {
        out[f.key] = q.get(f.key) || f.defaultValue || 'all';
      }
    }
    return out;
  });
  const [selected, setSelected] = useState(null);
  // Sort order, as the API takes it: `field` ascending, `-field` descending.
  // Undefined means "let the endpoint apply its own default", which is what
  // every screen did before sorting existed — so an untouched list is
  // ordered exactly as it always was.
  const [sort, setSort] = useState(defaultSort);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState(drawerTabs[0] || 'Overview');
  const [stats, setStats] = useState(null);

  const filterKey = JSON.stringify(values);
  const queryKey = JSON.stringify(initialQuery);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = { page, limit: pageSize, ...JSON.parse(queryKey) };
      if (sort) params.sort = sort;
      if (q.trim()) params.search = q.trim();
      for (const [k, v] of Object.entries(JSON.parse(filterKey))) {
        if (v && v !== 'all') params[k] = v;
      }
      const res = await axios.get(`${BASE_URL}${endpoint}`, { params, withCredentials: true });
      setRows(res.data?.data || []);
      setTotal(res.data?.total ?? 0);
      if (Array.isArray(res.data?.sortable)) setSortable(res.data.sortable);
      // The server reports the EFFECTIVE order. Adopting it means a stale
      // `?sort=` that it could not honour stops showing an active arrow.
      if (res.data?.sort !== undefined) setSort(res.data.sort || '');
    } catch (e) {
      // A 403 here is the feature gate, not a bug. Saying which sends the
      // reader to the right place instead of "something went wrong".
      const status = e?.response?.status;
      setErr(status === 403
        ? (e?.response?.data?.message || 'This module is not enabled for your organisation.')
        : (e?.response?.data?.message || e.message || 'Could not load this list'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [endpoint, page, pageSize, q, sort, filterKey, queryKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, filterKey]);

  // Headline figures for the whole book, independent of the page shown.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BASE_URL}${endpoint}/stats`, { withCredentials: true })
      .then(res => { if (!cancelled) setStats(res.data?.data || null); })
      .catch(() => { if (!cancelled) setStats(null); });
    return () => { cancelled = true; };
  }, [endpoint]);

  // Keep the drawer showing the freshest copy of the selected row after a
  // reload, rather than the snapshot taken when it was clicked.
  const current = useMemo(() => {
    if (!selected) return null;
    return rows.find(r => String(r._id) === String(selected._id)) || selected;
  }, [rows, selected]);

  // ?selected=<id> opens the drawer on that row. The detail screens the first
  // build shipped are redirected here, so a link someone saved still lands on
  // the right record rather than 404ing — the design has no detail PAGE, but a
  // record still needs an address.
  const location = useLocation();
  const history = useHistory();
  const wanted = new URLSearchParams(location.search).get('selected');
  // What the user has explicitly dismissed.
  //
  // Without this the drawer could not be closed at all: onClose set
  // `selected` to null, this effect re-ran while ?selected= was still in the
  // URL, saw no selection, and restored the row that had just been
  // dismissed. A ref rather than state - it must not itself cause a render.
  const dismissedRef = useRef(null);
  useEffect(() => {
    if (!wanted || selected) return;
    if (dismissedRef.current === wanted) return;
    const hit = rows.find(r => String(r._id) === wanted);
    if (hit) { setSelected(hit); return; }
    // Not on this page — fetch the one record directly rather than paging
    // through the list looking for it.
    let cancelled = false;
    axios.get(`${BASE_URL}${endpoint}/${wanted}`, { withCredentials: true })
      .then(res => { if (!cancelled && res.data?.data) setSelected(res.data.data); })
      // A silent catch here is why following a link to a record could land on
      // a plain list with nothing open and nothing said — the record had been
      // voided, or the caller lacked the entitlement, and the page simply did
      // not mention it. Say so instead.
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setErr(status === 404
          ? 'That record is no longer in this list — it may have been voided or cancelled.'
          : (e?.response?.data?.message || 'Could not open that record.'));
      });
    return () => { cancelled = true; };
  }, [wanted, rows, selected, endpoint]);

  // Keep the address bar in step with what is open, so the drawer is
  // shareable and the browser's back button closes it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const has = params.get('selected');
    const id = current ? String(current._id) : null;
    if (has === id) return;
    if (id) params.set('selected', id); else params.delete('selected');
    history.replace({ pathname: location.pathname, search: params.toString() });
    // location.search is read through `params`; adding it to the deps would
    // re-fire this effect on the change it just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, history, location.pathname]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const header = current && drawerHeader ? drawerHeader(current) : null;

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.body}>
          <div style={S.main}>
            <PageHeader
              title={title}
              subtitle={subtitle}
              actions={typeof actions === 'function'
                ? actions({ openCreate: () => setCreating(true) })
                : actions}
            />

            {renderKpis && <KpiRow>{renderKpis(stats)}</KpiRow>}

            <FilterBar
              search={q}
              onSearch={setQ}
              placeholder={searchPlaceholder}
              filters={filters}
              values={values}
              onFilter={(k, v) => setValues(prev => ({ ...prev, [k]: v }))}
              right={loading ? 'Loading…' : `${total} ${total === 1 ? 'record' : 'records'}`}
            />

            {err && (
              <div style={{
                border: `1px solid ${C.border}`, background: '#fff7f7', color: C.red,
                borderRadius: 10, padding: '11px 14px', fontSize: 13, marginBottom: 14,
              }}>{err}</div>
            )}

            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              sort={sort}
              sortableFields={sortable}
              onSort={(next) => { setSort(next); setPage(1); }}
              selectedId={current?._id}
              onSelect={(row) => {
                // Clicking a row is an explicit request to open it, even if
                // it is the row just dismissed - so clear the guard first.
                dismissedRef.current = null;
                setSelected(row);
                setTab(drawerTabs[0] || 'Overview');
              }}
              empty={err ? 'Nothing to show.' : emptyMessage}
            />

            {pages > 1 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 14 }}>
                <button type='button' disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  style={{ padding: '7px 13px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.body, fontSize: 12.5, fontWeight: 600, cursor: page <= 1 ? 'default' : 'pointer' }}>
                  Previous
                </button>
                <span style={{ fontSize: 12.5, color: C.muted }}>Page {page} of {pages}</span>
                <button type='button' disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                  style={{ padding: '7px 13px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.body, fontSize: 12.5, fontWeight: 600, cursor: page >= pages ? 'default' : 'pointer' }}>
                  Next
                </button>
              </div>
            )}
          </div>

          <CreateForm
            open={creating}
            config={createForm}
            onClose={() => setCreating(false)}
            onCreated={(row) => {
              setCreating(false);
              // Reload so the new row appears with whatever the server
              // computed for it — its reference number, most obviously,
              // which the form never sent.
              load();
              if (row) {
                dismissedRef.current = null;
                setSelected(row);
                setTab(drawerTabs[0] || 'Overview');
              }
            }}
          />

          <Drawer
            open={!creating && !!current}
            onClose={() => {
              // Record the dismissal BEFORE clearing the selection, so the
              // deep-link effect cannot race in and reopen the same row.
              dismissedRef.current = current ? String(current._id) : null;
              setSelected(null);
            }}
            eyebrowTitle={header?.title}
            badge={header?.badge}
            lines={header?.lines || []}
            tabs={drawerTabs}
            tab={tab}
            onTab={setTab}
          >
            {current && drawer ? drawer(current, { tab, setTab, reload: load }) : null}
          </Drawer>
        </div>
      </div>
    </AppLayout>
  );
}
